// src/pages/RefundPolicyPage.jsx
import React from "react";
import { SCHOOL } from "../config";

export default function RefundPolicyPage() {
  return (
    <div style={{ maxWidth: "760px", margin: "0 auto", padding: "40px 20px 80px", lineHeight: 1.7, color: "#1F2937" }}>
      <h1 style={{ color: "#7B1515", fontSize: "1.6rem", marginBottom: "4px" }}>Refunds &amp; Cancellations</h1>
      <p style={{ color: "#6B7280", fontSize: ".85rem", marginBottom: "28px" }}>
        Last updated: October 2026 · Applies to payments made to {SCHOOL.name}
      </p>

      <p>
        This policy explains when fees paid to {SCHOOL.name} can be refunded, and how to request one. It covers
        both online payments made through the Student Portal and payments made directly to the school.
      </p>

      <h2 style={sectionStyle}>What you are paying for</h2>
      <p>
        All fees are for <strong>in-person music classes</strong> taught at our premises in Pune, or for school
        events and activities. No digital content or online service is sold.
      </p>
      <p>
        Monthly tuition is charged per student and varies by batch, according to the type of batch, the
        examination level and the level of expertise taught. Your applicable amount is shown in the Student
        Portal. All amounts are in Indian Rupees (INR).
      </p>

      <h2 style={sectionStyle}>Your first month is normally not paid in advance</h2>
      <p>
        We do not normally ask for an upfront fee when a student joins. In most cases a new student attends for a
        month first, with that month's fee payable once it is complete. From the following month onwards, fees are
        payable in advance by the 7th of the month, one month at a time — we do not collect more than a single
        month ahead.
      </p>
      <p>
        This means a student who decides, after trying it, that classes are not for them can usually simply stop —
        with nothing paid in advance to reclaim. Music is an art, and we believe interest in it has to be kindled
        rather than enforced. We would rather a student stayed because they wanted to.
      </p>

      <h2 style={sectionStyle}>When we will refund</h2>
      <ul style={listStyle}>
        <li><strong>Duplicate payment.</strong> If you are charged twice for the same month — for example an
          online payment plus a cash payment for the same period — we refund the duplicate in full.</li>
        <li><strong>Incorrect amount charged.</strong> If you were billed more than the fee applicable to you,
          we refund the difference.</li>
        <li><strong>Event cancelled by the school.</strong> If an event, competition or performance you have paid
          for is cancelled by us, we refund the fee in full within one week.</li>
      </ul>

      <h2 style={sectionStyle}>If money was deducted but your payment is not showing</h2>
      <p>
        Occasionally a payment succeeds at the bank but does not appear in the Portal — a gateway or connectivity
        glitch. <strong>In this case we record the payment manually rather than refunding it</strong>, so your
        month is correctly marked as paid and your receipt is issued. You are not asked to pay again.
      </p>
      <p>
        Contact us with the date, amount and — if you have it — the transaction or order ID, and we will reconcile
        it against our records.
      </p>

      <h2 style={sectionStyle}>When we do not refund</h2>
      <ul style={listStyle}>
        <li><strong>Once a month's fee is paid, it is non-refundable.</strong> This applies even if the student
          attends few classes or stops partway through that month. The place in the batch has been reserved and
          taught.</li>
        <li><strong>Classes missed by the student</strong> are not refunded and are not carried forward. Fees
          reserve a place in a batch; they are not charged per class attended.</li>
        <li><strong>Event and activity fees</strong> are not refundable once paid, except where the school
          cancels the event. These are generally committed to venues, organisers or examining bodies in advance.</li>
      </ul>
      <p>
        We apply this straightforwardly because of how our fees are structured: a new student can attend a full
        month and leave without paying anything at all, and no one is ever asked for more than one month at a
        time. The opportunity to step away without cost comes before payment, not after it.
      </p>

      <h2 style={sectionStyle}>Class cancellations and replacements</h2>
      <ul style={listStyle}>
        <li><strong>Public holidays</strong> are holidays for the school. These classes are not rescheduled and
          no fee adjustment applies.</li>
        <li><strong>If we cancel a class</strong> for any other reason — teacher unavailability, for example — we
          offer a replacement class at a mutually convenient time.</li>
        <li><strong>Extra classes for events, competitions and performances</strong> are provided at no
          additional charge. When students are preparing for a programme we take additional sessions as needed,
          and these are never billed separately.</li>
      </ul>

      <h2 style={sectionStyle}>How to request a refund</h2>
      <p>Contact us with:</p>
      <ul style={listStyle}>
        <li>The student's name</li>
        <li>The payment date and amount</li>
        <li>The order or transaction ID, if you have one (shown on your receipt in the Portal)</li>
        <li>The reason for the request</li>
      </ul>
      <p>
        We respond within <strong>5 working days</strong>. Where a refund is approved, it is processed within{" "}
        <strong>10 working days</strong> of approval. Refunds are returned to the original payment method, and
        your bank may take a few further days to credit it. Refunds for cancelled events are processed within one
        week.
      </p>

      <h2 style={sectionStyle}>Contact us</h2>
      <p>
        {SCHOOL.name}<br />
        {SCHOOL.address}<br />
        Email: <a href={`mailto:${SCHOOL.email}`} style={linkStyle}>{SCHOOL.email}</a><br />
        Phone: <a href={`tel:${SCHOOL.phone.replace(/\s/g, "")}`} style={linkStyle}>{SCHOOL.phone}</a>
      </p>
      <p style={{ marginTop: "28px", fontSize: ".9rem" }}>
        See also our <a href="/terms" style={linkStyle}>Terms &amp; Conditions</a> and{" "}
        <a href="/privacy" style={linkStyle}>Privacy Policy</a>.
      </p>
    </div>
  );
}

const sectionStyle = { color: "#7B1515", fontSize: "1.05rem", marginTop: "28px", marginBottom: "8px" };
const listStyle    = { paddingLeft: "20px", marginBottom: "12px" };
const linkStyle    = { color: "#7B1515", fontWeight: 600 };
